///
/// EarthBrowser
///
/// Created by Matt Giger on 1/7/2026
/// Copyright (c) 2026 Geodata Labs
///

import { trig } from "@stovokor/math/math"

export class WebMercatorTileId {
	public static key(z: number, y: number, x: number): string {
		return z + '_' + y + '_' + x
	}

	readonly key: string

	constructor(readonly z: number, readonly y: number, readonly x: number) {
		this.key = WebMercatorTileId.key(z, y, x)
	}

	public get parent(): WebMercatorTileId {
		return new WebMercatorTileId(this.z - 1, this.y >> 1, this.x >> 1)
	}

	public get children(): WebMercatorTileId[] {
		const z1 = this.z + 1
		const y2 = this.y << 1
		const x2 = this.x << 1
		return [
			new WebMercatorTileId(z1, y2, x2),
			new WebMercatorTileId(z1, y2, x2 + 1),
			new WebMercatorTileId(z1, y2 + 1, x2),
			new WebMercatorTileId(z1, y2 + 1, x2 + 1)
		]
	}

	// WebMercator tile grid is always 2^z by 2^z
	public get rows(): number {
		return 1 << this.z
	}

	public get cols(): number {
		return 1 << this.z
	}

	public static clampLatitudeRad(latRad: number): number {
		// WebMercator is undefined at the poles; standard clamp is ±85.05112878°
		// https://wiki.openstreetmap.org/wiki/Mercator
		const maxLat = 1.4844222297453323 // 85.05112878° in radians
		return Math.max(-maxLat, Math.min(maxLat, latRad))
	}

	/** Geographic center of the tile in radians: [longitude, latitude] */
	public get centerLonLatRad(): [number, number] {
		const n = 1 << this.z
		const lon = ((this.x + 0.5) / n) * trig.two_pi - trig.pi
		const v = (this.y + 0.5) / n
		const lat = Math.atan(Math.sinh(trig.pi * (1 - 2 * v)))
		return [lon, lat]
	}

	public static fromLonLatRad(z: number, lonRad: number, latRad: number): WebMercatorTileId {
		const n = 1 << z

		// Normalize longitude into [-π, π)
		let lon = lonRad
		while (lon < -trig.pi) lon += trig.two_pi
		while (lon >= trig.pi) lon -= trig.two_pi

		const lat = WebMercatorTileId.clampLatitudeRad(latRad)

		const u = (lon + trig.pi) / trig.two_pi // 0..1
		const v = 0.5 - Math.log(Math.tan(trig.pi / 4 + lat / 2)) / trig.two_pi // 0..1

		const x = Math.max(0, Math.min(n - 1, Math.floor(u * n)))
		const y = Math.max(0, Math.min(n - 1, Math.floor(v * n)))

		return new WebMercatorTileId(z, y, x)
	}
}


